/**
 * Loans List Page
 * Display and manage all loans
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

import { fetchLoans } from '../loanSlice';
import Table from '../../../components/common/Table/Table';
import Button from '../../../components/common/Button/Button';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDate } from '../../../utils/formatDate';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import PERMISSIONS from '../../../constants/permissions';

const LoansList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loans, isLoading } = useSelector((state) => state.loans);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    dispatch(fetchLoans());
  }, [dispatch]);

  const columns = [
    { header: 'Loan ID', accessorKey: 'loanId' },
    { header: 'Member', accessorFn: (row) => `${row.member?.firstName} ${row.member?.lastName}` },
    { header: 'Amount', accessorKey: 'amount', cell: ({ row }) => formatCurrency(row.original.amount) },
    { header: 'Interest Rate', accessorKey: 'interestRate', cell: ({ row }) => `${row.original.interestRate}%` },
    { header: 'Term', accessorKey: 'termMonths', cell: ({ row }) => `${row.original.termMonths} months` },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => {
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          approved: 'bg-blue-100 text-blue-800',
          active: 'bg-green-100 text-green-800',
          completed: 'bg-gray-100 text-gray-800',
          defaulted: 'bg-red-100 text-red-800',
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[row.original.status] || 'bg-gray-100'}`}>
            {row.original.status}
          </span>
        );
      },
    },
    { header: 'Application Date', accessorKey: 'createdAt', cell: ({ row }) => formatDate(row.original.createdAt) },
  ];

  const handleRowClick = (row) => {
    navigate(`${ROUTES.LOANS.ROOT}/${row.id}`);
  };

  const canCreateLoan = hasPermission(PERMISSIONS.LOANS_CREATE);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Loans</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage and track all loans</p>
        </div>
        {canCreateLoan && (
          <Button variant="primary" onClick={() => navigate(ROUTES.LOANS.CREATE)}>
            <Plus className="w-4 h-4 mr-2" />
            Apply for Loan
          </Button>
        )}
      </div>

      {loans.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <Table data={loans} columns={columns} isLoading={isLoading} onRowClick={handleRowClick} />
        </div>
      ) : (
        <EmptyState
          title="No loans found"
          description="Start by creating a new loan application."
          actionLabel={canCreateLoan ? 'Apply for Loan' : null}
          onAction={canCreateLoan ? () => navigate(ROUTES.LOANS.CREATE) : null}
        />
      )}
    </div>
  );
};

export default LoansList;
